import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Envelope, Lock } from "@phosphor-icons/react";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/dashboard");
        } catch (err) {
            toast.error(err?.message || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout withFooter={false}>
            <Seo title="Login" path="/login" noIndex />
            <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-3 text-center">
                        ▌ ACCESS
                    </div>
                    <h1 className="text-4xl font-black uppercase tracking-tighter text-center mb-10">
                        Login
                    </h1>
                    <form
                        onSubmit={onSubmit}
                        className="space-y-5 bg-card border border-border p-8"
                        data-testid="login-form"
                    >
                        <div className="space-y-2">
                            <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest text-zinc-400">
                                Email
                            </Label>
                            <div className="relative">
                                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="pl-10 rounded-none bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary"
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="font-mono text-xs uppercase tracking-widest text-zinc-400">
                                Password
                            </Label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                <Input
                                    id="password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="pl-10 rounded-none bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary"
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider h-11"
                            data-testid="login-submit-btn"
                        >
                            {submitting ? "Signing in..." : "Sign In"}
                        </Button>
                        <div className="flex justify-between text-xs font-mono uppercase tracking-widest pt-2">
                            <Link to="/forgot-password" className="text-zinc-400 hover:text-primary transition-snappy" data-testid="forgot-password-link">
                                Forgot password
                            </Link>
                            <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                                Create account →
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
}
